import React, { createContext, useState, useEffect, useContext } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type ConsentOptions = {
  marketing: boolean;
  analytics: boolean;
  necessary: boolean;
};

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  userConsent: ConsentOptions;
  updateUserConsent: (consent: Partial<ConsentOptions>) => Promise<void>;
  hasConsented: (type: keyof ConsentOptions) => boolean;
  signOut: () => Promise<void>;
};

const defaultConsent: ConsentOptions = {
  marketing: false,
  analytics: false,
  necessary: true, // Necessary cookies are always required
};

export const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  userConsent: defaultConsent,
  updateUserConsent: async () => {},
  hasConsented: () => false,
  signOut: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userConsent, setUserConsent] = useState<ConsentOptions>(
    () => JSON.parse(localStorage.getItem('userConsent') || JSON.stringify(defaultConsent))
  );
  const { toast } = useToast();

  useEffect(() => {
    localStorage.setItem('userConsent', JSON.stringify(userConsent));
  }, [userConsent]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log('Auth state change:', event);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setLoading(false);

        if (event === 'SIGNED_OUT') {
          localStorage.removeItem('user-preferences');
          
          if (userConsent.analytics) {
            console.log('Analytics: User signed out');
          }
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [userConsent.analytics]);

  const updateUserConsent = async (consent: Partial<ConsentOptions>) => {
    try {
      const newConsent = { ...userConsent, ...consent };
      setUserConsent(newConsent);
      
      if (user) {
        const { error } = await supabase.auth.updateUser({
          data: { 
            consent_settings: newConsent 
          }
        });
          
        if (error) throw error;
      }
      
      toast({
        title: 'Consent Updated',
        description: 'Your privacy preferences have been saved.',
      });
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error updating consent:', error);
      toast({
        title: 'Error',
        description: 'Failed to update privacy preferences.',
        variant: 'destructive',
      });
      return Promise.reject(error);
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      return Promise.resolve();
    } catch (error) {
      console.error('Error signing out:', error);
      return Promise.reject(error);
    }
  };

  const hasConsented = (type: keyof ConsentOptions) => {
    return userConsent[type] === true;
  };

  const value = {
    session,
    user,
    loading,
    userConsent,
    updateUserConsent,
    hasConsented,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
